import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useStore } from '../lib/store'
import {
  tierMet,
  presentSignals,
  isThinCredential,
  CHECKPOINTS,
  checkpointLabel,
  liveVouchDevices,
  profileCorroboration,
} from '../lib/candidate'
import { canActOnPipeline } from '../lib/permissions'
import { sha256Hex, type ResumeCheckResult } from '../lib/liveVouchApi'
import { TRUST_TIERS, PAUSED_SIGNAL_IDS } from '../lib/constants'
import { fullDate, timeAgo } from '../lib/format'
import type { Candidate, PhoneRisk, Signal, TrustTier } from '../lib/types'
import { Avatar, Badge, Button, Card } from '../components/ui'
import { CredentialBadge } from '../components/Verification'
import { GhostMark } from '../components/GhostMark'
import {
  IconAlert,
  IconArrowRight,
  IconCheck,
  IconDoc,
  IconFace,
  IconLock,
  IconPin,
  IconSend,
  IconShield,
} from '../components/icons'
import './pages.css'

// ── Credential depth & freshness (§12.3) ─────────────────────────────────────
// Show the age and depth of a credential honestly so a recruiter can tell a
// thin, freshly-minted one from an established one — we surface thinness, we
// never accuse. A farm mints identities in bulk just before they're needed, so
// "created today · single signal" reads very differently from "established 8
// months · employer-verified · 3 relationships". The relationship count is an
// optional addend that the trust-chain layer (§12.4) fills in.
function DepthBanner({ c }: { c: Candidate }) {
  const present = presentSignals(c)
  const employerVerified = present.some((s) => s.id === 'employment')
  const relationships = c.vouchesIn?.length ?? 0 // trust-chain corroboration (§12.4)
  const thin = isThinCredential(c)

  const facts = [`${present.length} signal${present.length === 1 ? '' : 's'}`]
  if (employerVerified) facts.push('employer-verified')
  if (relationships > 0) facts.push(`${relationships} relationship${relationships === 1 ? '' : 's'}`)

  return (
    <Card>
      <div className="depth">
        <div className={`depth__age${thin ? ' is-thin' : ''}`}>
          {c.establishedAt
            ? `${thin ? 'Created' : 'Established'} ${timeAgo(c.establishedAt)}`
            : 'Credential origin date unknown'}
          {c.establishedAt && <span className="depth__on"> · {fullDate(c.establishedAt)}</span>}
        </div>
        <div className="depth__facts">{facts.join(' · ')}</div>
        {thin && (
          <div className="depth__note">
            <IconAlert size={13} /> Thin credential — newly created with little corroboration. Not a red flag on its
            own, just younger and less deep than an established one.
          </div>
        )}
      </div>
    </Card>
  )
}

// ── Risk flags (negative signals) ────────────────────────────────────────────
// The inverse of the trust ladder: cautions a recruiter weighs by hand, never
// folded into the tier. The first is `shared_device` (anti-interview-farm) — one
// device or number behind several identities. Framed honestly: a flag to review,
// not a verdict (shared/family hardware is a legitimate false positive), so the
// copy says "review," never "fraud."
function RiskFlags({ c }: { c: Candidate }) {
  const flags = c.riskFlags ?? []
  if (!flags.length) return null
  return (
    <Card>
      <div className="section-title">Review flags</div>
      <div className="riskflags">
        {flags.map((f) => (
          <div key={f.id} className={`riskflag riskflag--${f.severity}`}>
            <IconAlert size={16} />
            <div className="riskflag__body">
              <div className="riskflag__head">
                {f.label}
                {typeof f.count === 'number' && f.count > 0 && (
                  <Badge color="var(--c-error)" bg="var(--c-error-bg)">
                    {f.count + 1} identities
                  </Badge>
                )}
              </div>
              <div className="riskflag__detail">{f.detail}</div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  )
}

// ── Trust ladder ───────────────────────────────────────────────────────────
function TrustLadder({ c }: { c: Candidate }) {
  return (
    <Card>
      <div className="section-title">Trust ladder</div>
      <div className="ladder">
        {([1, 2, 3] as TrustTier[]).map((tier) => {
          const met = tierMet(c, tier)
          const meta = TRUST_TIERS[tier]
          return (
            <div key={tier} className={`rung t${tier}${met ? ' is-met' : ' rung--pending'}`}>
              <div className="rung__mark">
                <span className="rung__num">{met ? <IconCheck size={15} /> : tier}</span>
              </div>
              <div className="rung__body">
                <div className="rung__title">
                  {meta.name}
                  {met && <Badge color="var(--cred-live)" bg="var(--cred-live-bg)">Met</Badge>}
                </div>
                <div className="rung__blurb">{meta.blurb}</div>
              </div>
            </div>
          )
        })}
      </div>
    </Card>
  )
}

// ── Trust chain (§12.4) ──────────────────────────────────────────────────────
function vouchYears(start?: string, end?: string): string {
  if (!start) return ''
  return `${start.slice(0, 4)}–${end ? end.slice(0, 4) : 'present'}`
}

// The most farm-resistant signal: verified people who attached their own
// accountable identity to corroborate this candidate. We draw a hard line
// between a *confirmed* overlap (both sides employer-verified, dates intersect)
// and a merely *claimed* one (the voucher's word) — the same anti-overclaim
// discipline as `proves` / `doesNotProve`.
function TrustChain({ c }: { c: Candidate }) {
  const vouches = c.vouchesIn ?? []
  if (vouches.length === 0) return null
  const first = c.name.split(' ')[0]
  return (
    <Card>
      <div className="section-title">Trust chain · {vouches.length}</div>
      <p className="rung__blurb" style={{ marginBottom: '0.85rem' }}>
        Verified people who staked their own accountable identity to corroborate {first}. A farm can mint a verified
        human — not a verified former colleague with confirmed overlapping tenure.
      </p>
      <div className="vouch-list">
        {vouches.map((v) => {
          const confirmed = v.tenureProof.mode === 'confirmed'
          const years = vouchYears(v.overlapStart, v.overlapEnd)
          return (
            <div key={v.id} className="vouch">
              <Avatar hue={v.voucherHue ?? 220} initials={v.voucherInitials ?? '··'} size={38} />
              <div className="vouch__body">
                <div className="vouch__head">
                  <span className="vouch__name">{v.voucherName ?? 'Verified peer'}</span>
                  <Badge
                    color={confirmed ? 'var(--cred-live)' : 'var(--c-warn)'}
                    bg={confirmed ? 'var(--cred-live-bg)' : 'var(--c-warn-bg)'}
                  >
                    {confirmed ? 'Confirmed overlap' : 'Claimed overlap'}
                  </Badge>
                </div>
                <div className="vouch__rel">
                  {v.relationship} at {v.claimedEmployer}
                  {years && ` · ${years}`}
                </div>
                <div className="vouch__proof">
                  {confirmed
                    ? 'Both hold employer-verified credentials at this company with overlapping dates.'
                    : 'The voucher’s own word — not independently confirmed by an employer.'}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </Card>
  )
}

// The colour a Telesign risk band reads at. Low risk is the credential-green;
// medium warns; high (or a "block" recommendation) is an error. Unknown stays
// neutral — we never imply a verdict we don't have.
function riskColor(risk: PhoneRisk): string {
  const level = (risk.level || '').toLowerCase()
  if (risk.recommendation === 'block' || level === 'high') return 'var(--c-error)'
  // A VOIP/burner line never reads as credential-green — it's a fraud tell on its
  // own, so it floors at the warn colour even if the reputation score came back low.
  if (risk.recommendation === 'flag' || level === 'medium' || risk.voip) return 'var(--c-warn)'
  if (level === 'low' || risk.recommendation === 'allow') return 'var(--cred-live)'
  return 'var(--c-dim)'
}

// The Telesign assessment captured alongside a verified phone, shown as an
// attributable line under the proof: the recruiter sees the risk verdict and the
// line's provenance without ever seeing the number itself. The label reflects
// the tiered gate — a clean mobile clears the cheap PhoneID check ("cleared at
// gate"); only a risky number gets the full Intelligence risk score.
function PhoneRiskLine({ risk }: { risk: PhoneRisk }) {
  const color = riskColor(risk)
  // Lead the line type with an explicit "VOIP" when it's a burner line, so the
  // recruiter reads the fraud tell at a glance rather than parsing the raw type.
  const lineType = risk.voip ? 'VOIP' : risk.phoneType
  const meta = [lineType, risk.carrier].filter(Boolean).join(' · ')
  const label = risk.intelligenceRan
    ? `Phone risk score${[risk.level, risk.recommendation].filter(Boolean).join(' · ') ? `: ${[risk.level, risk.recommendation].filter(Boolean).join(' · ')}` : ''}`
    : 'Phone network check: cleared at gate'
  return (
    <div className="signal-item__risk">
      <span className="signal-item__risk-dot" style={{ background: color }} />
      <span>
        <b style={{ color }}>{label}</b>
        {meta ? ` · ${meta}` : ''}
      </span>
    </div>
  )
}

// ── "Show your work" signal list ─────────────────────────────────────────────
function SignalRow({ s }: { s: Signal }) {
  return (
    <div className={`signal-item${s.present ? '' : ' signal-item--absent'}`}>
      <div className={`signal-item__icon ${s.present ? 'is-on' : 'is-off'}`}>
        {s.present ? <IconCheck size={16} /> : <IconLock size={14} />}
      </div>
      <div className="signal-item__body">
        <div className="signal-item__head">
          <span className="signal-item__label">{s.label}</span>
          {s.source && <span className="signal-item__src">via {s.source}</span>}
          {!s.present && <Badge color="var(--c-faint)" bg="var(--surface-2)">Not on file</Badge>}
        </div>
        <div className="signal-item__proves">{s.proves}</div>
        {s.present && s.doesNotProve && (
          <div className="signal-item__limit">
            <IconAlert size={13} /> Does not prove: {s.doesNotProve}
          </div>
        )}
        {s.present && s.risk && <PhoneRiskLine risk={s.risk} />}
        {s.present && s.profile && <ProfileFactsLine profile={s.profile} />}
      </div>
      {s.present && s.verifiedAt && <div className="signal-item__date">{fullDate(s.verifiedAt)}</div>}
    </div>
  )
}

// The verified facts an OAuth identity signal (GitHub / LinkedIn) carries, shown
// inline beneath the row. The cross-platform read lives in ProfileCorroborationCard.
function ProfileFactsLine({ profile }: { profile: NonNullable<Signal['profile']> }) {
  const bits = [profile.name, profile.username && `@${profile.username}`].filter(Boolean).join(' · ')
  return (
    <div className="signal-item__risk">
      <span className="signal-item__risk-dot" style={{ background: 'var(--c-ok, #10b981)' }} />
      <span>
        {profile.url ? (
          <a href={profile.url} target="_blank" rel="noopener noreferrer">
            {bits}
          </a>
        ) : (
          bits
        )}
        {profile.active === false ? ' — no recent activity' : ''}
      </span>
    </div>
  )
}

// Cross-corroboration across the connected GitHub/LinkedIn profiles: independent
// accounts agreeing on the same human. A name mismatch is surfaced for a human to
// review — never an auto-block. Derived on read via profileCorroboration().
function ProfileCorroborationCard({ c }: { c: Candidate }) {
  const result = profileCorroboration(c)
  if (!result) return null
  const tone =
    result.verdict === 'corroborated'
      ? { label: 'Corroborated', color: 'var(--c-ok, #10b981)' }
      : result.verdict === 'mismatch'
        ? { label: 'Review', color: 'var(--c-warn, #f59e0b)' }
        : { label: '1 profile', color: 'var(--c-faint)' }
  const stateColor = (s: 'pass' | 'info' | 'fail') =>
    s === 'pass' ? 'var(--c-ok, #10b981)' : s === 'fail' ? 'var(--c-warn, #f59e0b)' : 'var(--c-faint)'
  return (
    <Card>
      <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        Cross-corroboration
        <Badge color={tone.color} bg="var(--surface-2)">
          {tone.label}
        </Badge>
      </div>
      <p className="rung__blurb" style={{ marginBottom: '0.85rem' }}>
        {result.summary} Independent accounts agreeing on the same person is stronger than any one alone.
      </p>
      <div className="signal-list">
        {result.checks.map((check) => (
          <div className="signal-item__risk" key={check.label}>
            <span className="signal-item__risk-dot" style={{ background: stateColor(check.state) }} />
            <span>
              <b style={{ color: stateColor(check.state) }}>{check.label}</b> — {check.detail}
            </span>
          </div>
        ))}
      </div>
    </Card>
  )
}

// ── Live Vouch challenge-response ────────────────────────────────────────────
type LVPhase = 'idle' | 'sending' | 'accepted' | 'scanning' | 'confirmed' | 'timeout'

// How long the console waits on a real device to complete a Live Vouch before it
// surfaces a re-send. The request marker persists on the account regardless, so a
// candidate can still complete it later — this only governs the live waiting UI.
const LIVE_VOUCH_WAIT_MS = 120_000

function codeFor(id: string): string {
  let h = 0
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) & 0xffffff
  return (100000 + (h % 900000)).toString()
}

// A short, stable fingerprint for a confirming device key (§12.2). We never show
// the raw key — just enough to tell "same device" from "a different one".
function deviceTag(key: string): string {
  return codeFor(key).slice(0, 4)
}

function LiveVouchPanel({ c }: { c: Candidate }) {
  const { requestLiveVouch, confirmLiveVouch, session } = useStore()
  const canAct = canActOnPipeline(session.recruiter.seatRole)
  const first = c.name.split(' ')[0]

  const history = c.liveVouch.history ?? []
  const devices = liveVouchDevices(c) // distinct keys, first-seen order
  const multiDevice = devices.length > 1
  const deviceName = (key: string) => `Device ${String.fromCharCode(65 + devices.indexOf(key))}`

  // Default the next checkpoint to the first stage not yet confirmed (screen →
  // before-technical → offer), falling back to the last once all are done.
  const used = new Set(history.map((e) => e.checkpoint))
  const nextCp = CHECKPOINTS.find((cp) => !used.has(cp.value)) ?? CHECKPOINTS[CHECKPOINTS.length - 1]
  const [checkpoint, setCheckpoint] = useState<string>(nextCp.value)
  const [phase, setPhase] = useState<LVPhase>('idle')
  const timers = useRef<number[]>([])
  // History length captured at request time — the real confirmation is detected by
  // this count growing (the backend appends the device's event to the row).
  const baseCount = useRef(0)

  // A linked app account means a real device is on the other end: we push the
  // request and wait for the candidate's genuine, Secure-Enclave-signed confirmation
  // to arrive over realtime — never a scripted timeline. Recruiter-only fixtures
  // (no device) keep the simulated walk-through so the console still demonstrates it.
  const linked = !!c.accountId

  const clearTimers = () => {
    timers.current.forEach(clearTimeout)
    timers.current = []
  }
  useEffect(() => clearTimers, [])

  const start = useCallback(() => {
    const at = (ms: number, fn: () => void) => timers.current.push(window.setTimeout(fn, ms))
    clearTimers()

    if (linked) {
      // Real loop. Push the request to the device; sit on "waiting" until the
      // candidate completes Face ID and the backend mirrors the confirmed event
      // onto this row (detected by the effect below). No fake accept/scan/confirm.
      baseCount.current = c.liveVouch.history?.length ?? 0
      requestLiveVouch(c.id, checkpoint)
      setPhase('sending')
      at(LIVE_VOUCH_WAIT_MS, () => setPhase((p) => (p === 'confirmed' ? p : 'timeout')))
      return
    }

    // Demo fixture: no device to reach, so animate the flow for illustration.
    requestLiveVouch(c.id, checkpoint)
    setPhase('sending')
    at(1500, () => setPhase('accepted'))
    at(3000, () => setPhase('scanning'))
    at(5200, () => {
      confirmLiveVouch(c.id, { checkpoint })
      setPhase('confirmed')
    })
  }, [linked, c.id, c.liveVouch.history, checkpoint, requestLiveVouch, confirmLiveVouch])

  // Real loop: the moment the candidate's device confirms, the backend appends the
  // event to this row and realtime streams it in — flip to confirmed (recovering
  // from a timeout if the response was just slow). Demo fixtures self-confirm above.
  useEffect(() => {
    if (!linked || (phase !== 'sending' && phase !== 'timeout')) return
    const len = c.liveVouch.history?.length ?? 0
    if (len <= baseCount.current) return
    clearTimers()
    const last = c.liveVouch.history?.[len - 1]
    if (last?.checkpoint) setCheckpoint(last.checkpoint)
    setPhase('confirmed')
  }, [linked, phase, c.liveVouch.history])

  // Return to the picker for another checkpoint, advancing the default forward.
  const again = useCallback(() => {
    setPhase('idle')
    setCheckpoint((cur) => {
      const done = new Set([...(c.liveVouch.history ?? []).map((e) => e.checkpoint), cur])
      return (CHECKPOINTS.find((cp) => !done.has(cp.value)) ?? CHECKPOINTS[CHECKPOINTS.length - 1]).value
    })
  }, [c.liveVouch.history])

  const repeat = history.length > 0

  const vouchHistory =
    history.length === 0 ? null : (
      <div className="lv-history">
        <div className="lv-history__head">
          <span>Vouch history · {history.length}</span>
          {multiDevice && (
            <Badge color="var(--c-warn)" bg="var(--c-warn-bg)">
              {devices.length} devices · review
            </Badge>
          )}
        </div>
        {history.map((e) => (
          <div key={e.id} className="lv-event">
            <span className="lv-event__cp">{checkpointLabel(e.checkpoint)}</span>
            <span className="lv-event__at">{fullDate(e.confirmedAt)}</span>
            <span className={`lv-event__dev${e.deviceKeyB64 === devices[0] ? '' : ' is-flag'}`}>
              {deviceName(e.deviceKeyB64)} · {deviceTag(e.deviceKeyB64)}
            </span>
          </div>
        ))}
        {multiDevice && (
          <div className="lv-flag">
            <IconAlert size={13} /> A different device confirmed at a later checkpoint. Allowed under the multi-device
            policy — flagged for review, never blocked.
          </div>
        )}
      </div>
    )

  const checkpointPicker = (
    <div className="lv-cps" role="group" aria-label="hiring checkpoint">
      {CHECKPOINTS.map((cp) => (
        <button
          key={cp.value}
          type="button"
          className={`lv-cp${cp.value === checkpoint ? ' is-active' : ''}`}
          onClick={() => setCheckpoint(cp.value)}
          disabled={!canAct}
        >
          {cp.label}
        </button>
      ))}
    </div>
  )

  if (phase === 'idle') {
    return (
      <div className="livevouch">
        <div className="lv-stage">
          <GhostMark size={64} glow tint="live" />
          <div className="lv-title">{repeat ? `Re-confirm it's still ${first}` : 'Run a Live Vouch'}</div>
          <div className="lv-sub">
            {repeat
              ? 'Re-running the Face ID challenge at each stage is the continuity a candidate-swap can’t fake — the same verified human has to show up every time.'
              : `A real-time Face ID challenge-response with ${first}. The strongest signal on the ladder — proves a present, live human, right now.`}
          </div>
        </div>
        {checkpointPicker}
        <Button
          variant="primary"
          block
          icon={<IconFace size={18} />}
          onClick={start}
          disabled={!canAct}
          title={!canAct ? 'Viewers have read-only access' : undefined}
        >
          {repeat ? `Re-confirm · ${checkpointLabel(checkpoint)}` : 'Request Live Vouch'}
        </Button>
        {vouchHistory}
      </div>
    )
  }

  if (phase === 'timeout') {
    return (
      <div className="livevouch">
        <div className="lv-stage">
          <GhostMark size={64} tint="live" />
          <div className="lv-title">Waiting on {first}’s device</div>
          <div className="lv-sub">
            The request is on {first}’s phone — they can complete the Face ID challenge whenever they’re ready, and
            it’ll confirm here the moment they do. Re-send if you’d like to nudge them.
          </div>
        </div>
        <Button variant="primary" block icon={<IconFace size={18} />} onClick={start} disabled={!canAct}>
          Re-send request · {checkpointLabel(checkpoint)}
        </Button>
        {vouchHistory}
      </div>
    )
  }

  const steps = [
    { key: 'sending', label: `Pushed challenge to ${first}'s device` },
    { key: 'accepted', label: `${first} accepted the request` },
    { key: 'scanning', label: 'Face ID + liveness challenge running' },
    { key: 'confirmed', label: 'Identity confirmed in real time' },
  ] as const
  const order: LVPhase[] = ['idle', 'sending', 'accepted', 'scanning', 'confirmed']
  const idx = order.indexOf(phase)

  const scanMod =
    phase === 'sending' || phase === 'accepted'
      ? 'lv-scan--waiting'
      : phase === 'scanning'
        ? 'lv-scan--scanning'
        : phase === 'confirmed'
          ? 'lv-scan--done'
          : ''

  return (
    <div className="livevouch">
      <div className="lv-stage">
        <div className={`lv-scan ${scanMod}`}>
          <span className="lv-scan__ring" />
          {phase === 'confirmed' ? <IconCheck size={34} /> : <IconFace size={34} />}
        </div>
        {phase === 'scanning' && (
          <div className="lv-code" aria-label="challenge code">
            {codeFor(c.id)
              .split('')
              .map((d, i) => (
                <span key={i} style={{ animationDelay: `${i * 60}ms` }}>
                  {d}
                </span>
              ))}
          </div>
        )}
        <div className="lv-title">
          {phase === 'confirmed'
            ? `Live Vouch confirmed · ${checkpointLabel(checkpoint)}`
            : phase === 'scanning'
              ? 'Matching challenge code…'
              : `Waiting for ${first}…`}
        </div>
        <div className="lv-sub">
          {phase === 'confirmed'
            ? `${first} passed a live Face ID challenge. Tier 3 — Deeply Verified.`
            : 'This happens on the candidate’s trusted device. No code or password is shared.'}
        </div>
      </div>

      <div className="lv-steps">
        {steps.map((s) => {
          const sIdx = order.indexOf(s.key as LVPhase)
          const state = phase === 'confirmed' || sIdx < idx ? 'is-done' : sIdx === idx ? 'is-active' : ''
          return (
            <div key={s.key} className={`lv-step ${state}`}>
              <span className="lv-step__tick">{state === 'is-done' ? <IconCheck size={11} /> : ''}</span>
              {s.label}
            </div>
          )
        })}
      </div>

      {phase === 'confirmed' && (
        <Button variant="ghost" block onClick={again} disabled={!canAct}>
          Re-confirm at another checkpoint
        </Button>
      )}
      {phase === 'confirmed' && vouchHistory}
    </div>
  )
}

// ── Résumé hash-match ────────────────────────────────────────────────────────
// The recruiter half of the device-signed résumé. The candidate signed their file's
// SHA-256 with their holder key and we kept the fingerprint, never the document. So
// the question this card answers is not "is there a record?" but "is the file I was
// SENT the one they signed?" — which requires the recruiter's own copy. We hash it
// here in the browser and send only the digest, so their copy never leaves either.
//
// Two independent facts come back and are shown as two lines, never merged: whether
// the file matches the fingerprint, and whether that fingerprint carries a signature
// that verifies. A match against a record nobody provably signed is NOT a pass.
function ResumeMatch({ c }: { c: Candidate }) {
  const { checkResume, session } = useStore()
  const canAct = canActOnPipeline(session.recruiter.seatRole)
  const fileRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [checked, setChecked] = useState<{ result: ResumeCheckResult; fileName: string; hash: string } | null>(null)

  // '—' is the store's "no résumé" sentinel (lib/pb.ts), not a fingerprint. And only
  // a FULL sha-256 digest can be checked against a file — demo and legacy rows carry
  // a shortened display fingerprint, which we still show but honestly cannot verify.
  const recordedHash = (c.resume.hash || '').trim()
  const hasRecord = recordedHash !== '' && recordedHash !== '—'
  const checkable = /^[a-f0-9]{64}$/i.test(recordedHash)

  const onPick = useCallback(
    async (file: File | undefined) => {
      if (!file) return
      setBusy(true)
      setError('')
      setChecked(null)
      try {
        const hash = await sha256Hex(file)
        const result = await checkResume(c.id, hash)
        setChecked({ result, fileName: file.name, hash })
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not check that file.')
      } finally {
        setBusy(false)
        if (fileRef.current) fileRef.current.value = ''
      }
    },
    [c.id, checkResume],
  )

  const verdict = (() => {
    if (!checked) return null
    const { match, signatureValid } = checked.result
    if (match === 'unverified')
      return {
        color: 'var(--c-faint)',
        bg: 'var(--surface-2)',
        label: 'Nothing to check',
        blurb: 'This candidate has not device-signed a résumé, so there is no fingerprint to compare against.',
      }
    if (match === 'mismatch')
      return {
        color: 'var(--c-error)',
        bg: 'var(--c-error-bg)',
        label: 'Does not match',
        blurb:
          'This file does not hash to the fingerprint on record — it is not the document the candidate signed, or it has been edited since. Review carefully before advancing.',
      }
    if (signatureValid === false)
      return {
        color: 'var(--c-error)',
        bg: 'var(--c-error-bg)',
        label: 'Signature invalid',
        blurb:
          'The file matches the fingerprint on record, but that record’s signature does not verify against the candidate’s device key. Treat it as unproven.',
      }
    if (signatureValid === null)
      return {
        color: 'var(--c-warn)',
        bg: 'var(--c-warn-bg)',
        label: 'Matches, signature unknown',
        blurb:
          'This file matches the fingerprint on record, but that record was stored without a signing key, so we cannot prove the candidate signed it. Ask them to re-sign to get a verifiable record.',
      }
    return {
      color: 'var(--c-success)',
      bg: 'var(--c-success-bg)',
      label: 'Verified match',
      blurb:
        'This is the exact file the candidate device-signed, and the signature verifies against their holder key. It has not been altered since.',
    }
  })()

  return (
    <Card>
      <div className="section-title">Résumé integrity</div>

      {hasRecord ? (
        <div className="resume">
          <div className="signal-item__icon is-on" style={{ background: 'var(--surface-2)', color: 'var(--c-accent)' }}>
            <IconDoc size={16} />
          </div>
          <div className="resume__file">
            <div className="resume__name">{c.resume.fileName || 'Signed résumé'}</div>
            <div className="resume__hash">sha256:{recordedHash}</div>
          </div>
        </div>
      ) : (
        <p className="rung__blurb">
          This candidate has not device-signed a résumé yet. Once they do, you can check any copy you receive against
          their signed fingerprint here.
        </p>
      )}

      {hasRecord && !checkable && (
        <p className="rung__blurb" style={{ marginTop: '0.85rem' }}>
          This record carries a shortened display fingerprint rather than a full digest, so there is nothing to check a
          file against. Ask the candidate to re-sign their résumé to get a verifiable record.
        </p>
      )}

      {hasRecord && checkable && (
        <>
          <p className="rung__blurb" style={{ marginTop: '0.85rem' }}>
            Upload the résumé you were sent to check it against that fingerprint. The file is hashed in your browser and
            never leaves it.
          </p>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.doc,.docx,.txt,.rtf,.md"
            style={{ display: 'none' }}
            onChange={(e) => void onPick(e.target.files?.[0])}
          />
          <Button
            variant="secondary"
            block
            style={{ marginTop: '0.85rem' }}
            icon={<IconShield size={15} />}
            disabled={!canAct || busy}
            title={!canAct ? 'Viewers have read-only access' : undefined}
            onClick={() => fileRef.current?.click()}
          >
            {busy ? 'Checking…' : checked ? 'Check another file' : 'Check a résumé I received'}
          </Button>
        </>
      )}

      {error && (
        <p className="rung__blurb" style={{ marginTop: '0.85rem', color: 'var(--c-error)' }}>
          {error}
        </p>
      )}

      {verdict && checked && (
        <>
          <div className="resume" style={{ marginTop: '0.85rem' }}>
            <div className="resume__file">
              <div className="resume__name">{checked.fileName}</div>
              <div className="resume__hash">sha256:{checked.hash}</div>
            </div>
            <Badge color={verdict.color} bg={verdict.bg}>
              {verdict.label}
            </Badge>
          </div>
          <p className="rung__blurb" style={{ marginTop: '0.85rem' }}>
            {verdict.blurb}
          </p>
        </>
      )}
    </Card>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────
export function CandidateProfile() {
  const { id } = useParams()
  const nav = useNavigate()
  const { candidates, unlock, sendVerifyRequests, logView, loadVouches, allowanceUsed, allowanceTotal, session } =
    useStore()
  const canAct = canActOnPipeline(session.recruiter.seatRole)
  const c = candidates.find((x) => x.id === id)

  // Log a view + load the trust-chain vouches once per candidate visit.
  useEffect(() => {
    if (c) {
      logView(c.id)
      loadVouches(c.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  if (!c) {
    return (
      <div className="page">
        <button className="back-link" onClick={() => nav('/candidates')}>
          <IconArrowRight size={15} /> Back to candidates
        </button>
        <Card>
          <p className="muted">That candidate isn’t in your pool anymore.</p>
        </Card>
      </div>
    )
  }

  const atCap = allowanceUsed >= allowanceTotal

  return (
    <div className="page">
      <button className="back-link" onClick={() => nav('/candidates')}>
        <IconArrowRight size={15} /> Back to candidates
      </button>

      <div className="phero">
        <Avatar hue={c.avatarHue} initials={c.initials} size={64} />
        <div className="phero__id">
          <div className="phero__name">
            {c.name}
            <GhostMark size={26} tint={c.state} breathing={c.state === 'live'} title={`Tier ${c.tier}`} />
          </div>
          <div className="phero__headline">{c.headline}</div>
          <div className="phero__meta">
            <CredentialBadge state={c.state} />
            <span className="cand-card__meta">
              <IconPin size={13} /> {c.location}
              <span className="dot-sep" />
              {c.yearsExperience} yrs experience
              <span className="dot-sep" />
              Active {timeAgo(c.lastActive)}
            </span>
          </div>
        </div>
      </div>

      <div className="pill-row" style={{ marginBottom: '1.25rem' }}>
        {c.topSkills.map((s) => (
          <span key={s} className="skill-pill">
            {s}
          </span>
        ))}
      </div>

      <div className="profile">
        <div className="stack">
          <DepthBanner c={c} />
          <RiskFlags c={c} />
          <TrustLadder c={c} />
          <TrustChain c={c} />

          <Card>
            <div className="section-title">What we verified — and what we didn’t</div>
            <div className="signal-list">
              {/* Hide PAUSED signals (gov_id/employment, on legal hold) so the console
                  never renders them as "verified" — incl. demo rows seeded before the
                  pause (M02). They're also excluded from tier gating (see tier.mjs). */}
              {c.signals.filter((s) => !PAUSED_SIGNAL_IDS.has(s.id)).map((s) => (
                <SignalRow key={s.id} s={s} />
              ))}
            </div>
          </Card>

          <ProfileCorroborationCard c={c} />
        </div>

        <div className="stack">
          {!c.unlocked ? (
            <Card>
              <div className="section-title">Locked profile</div>
              <p className="rung__blurb" style={{ marginBottom: '0.85rem' }}>
                Unlock to reveal full verification dates, run a Live Vouch, and check résumé integrity. Uses one
                verification from your monthly allowance.
              </p>
              <Button
                variant="primary"
                block
                icon={<IconLock size={15} />}
                disabled={atCap || !canAct}
                title={!canAct ? 'Viewers have read-only access' : atCap ? 'Monthly allowance reached' : undefined}
                onClick={() => unlock(c.id)}
              >
                {!canAct ? 'Read-only access' : atCap ? 'Allowance reached' : 'Unlock candidate'}
              </Button>
            </Card>
          ) : (
            <>
              <Card>
                <div className="section-title">Live Vouch</div>
                <LiveVouchPanel c={c} />
              </Card>

              <ResumeMatch c={c} />

              <Card>
                <div className="section-title">Advance</div>
                {c.verifyRequest === 'pending' ? (
                  <p className="rung__blurb">
                    <Badge color="var(--c-warn)" bg="var(--c-warn-bg)" dot>
                      Awaiting candidate
                    </Badge>{' '}
                    A self-verification request is out — you’ll see it land in Activity.
                  </p>
                ) : c.verifyRequest === 'completed' ? (
                  <p className="rung__blurb">
                    <Badge color="var(--cred-live)" bg="var(--cred-live-bg)" dot>
                      Completed
                    </Badge>{' '}
                    This candidate has completed every step you requested.
                  </p>
                ) : (
                  <>
                    <p className="rung__blurb" style={{ marginBottom: '0.85rem' }}>
                      Ask {c.name.split(' ')[0]} to complete the next verification step themselves.
                    </p>
                    <Button
                      variant="secondary"
                      block
                      icon={<IconSend size={15} />}
                      disabled={!canAct}
                      title={!canAct ? 'Viewers have read-only access' : undefined}
                      onClick={() => sendVerifyRequests([c.id])}
                    >
                      Send verification request
                    </Button>
                  </>
                )}
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
